import type { Me, Team } from '@yandex-int/k-common';
import * as api from '@yandex-int/k-common/client/api';
import { showNetworkError } from 'platform-components';
import ym from 'react-yandex-metrika';
import { Action, Dispatch } from 'redux';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { getCurrentYearCourseId } from 'common.components/course/selectors';
import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import { LESSON_VIEW_MODE } from 'common.components/lesson-view.store/typings';
import lessonActions from 'common.components/lesson/actions';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { setWizardVariables, setMe } from 'common.components/user/actions';
import { COPIED_AS_RNO_COUNT } from 'common.components/user/user.constants';
import { actions as coursesActions } from 'store/courses/actions';
import { getClassesWithCurrentSubject } from 'store/teacher-courses/selectors';
import { getSubjectFromPath, getTeamIdFromPath } from 'utils/path';

import actions from './actions';
import * as selectors from './selectors';
import { CopyLessonResponse, CREATION_TYPE } from './typings';

interface CopyLessonToTeam {
    sourceId: number;
    newName: string;
    teamId: number;
    creationType?: CREATION_TYPE;
    replaceProblems?: boolean;
    metrikaParams?: Record<string, any>;
}

export function* copyLessonSagaWatcher() {
    yield all([
        takeLatest(actions.copyLessonToTeamsRequest, copyLessonSaga),
        takeLatest(actions.copyCLesson, decideCopyLessonSaga),
        takeLatest(actions.copyLessonAsRNO, copyLessonAsRNOSaga),
    ]);
}

export function* decideCopyLessonSaga(action: ReturnType<typeof actions.copyCLesson>) {
    const { intl, history } = action.payload;

    const subject = getSubjectFromPath();

    const teams: Team[] = yield select(getClassesWithCurrentSubject, { subject });

    if (teams.length === 1) {
        yield put(
            actions.copyLessonToTeamsRequest({
                teamIds: [teams[0].id],
                intl,
                history,
            })
        );
    } else {
        yield put(actions.toggleModal({ shouldShowModal: true }));
    }
}

export function* copyLessonToTeam({
    sourceId,
    newName,
    teamId,
    creationType,
    replaceProblems,
    metrikaParams,
}: CopyLessonToTeam) {
    ym('reachGoal', 'teacher_lesson_copy_thisclass', metrikaParams);

    // нужно чтобы анимация проходила не менее 3 секунд
    // поэтому добавляем timeout для гарантии
    // если запросы на копирование занятия в сумме будут выполняться > 3 секунд, то ничего не произойдет
    // если < 3 секунд, то подождем до полных 3 секунду после выполнения getFullClessonRequest(см. ниже)
    const sleep = new Promise((resolve) => setTimeout(resolve, 3000));

    const mode =
        creationType === CREATION_TYPE.COPY_AS_RNO
            ? LESSON_VIEW_MODE.AFTER_COPYING_AS_RNO
            : LESSON_VIEW_MODE.AFTER_COPYING_FROM_OWN;

    yield put(actions.toggleModal({ shouldShowModal: false }));
    yield put(lessonViewActions.toggleModal(true));

    const {
        set: [{ clesson_id: clessonId, course_id: courseId }],
    } = yield call(api.postCopyCLesson, sourceId, newName, [teamId], { creationType, replaceProblems });
    yield put(function (dispatch: Dispatch<any>) {
        dispatch(
            lessonActions.getFullClessonRequest({
                clessonId,
                courseId,
                onLoad: async () => {
                    await sleep;

                    dispatch(lessonViewActions.setMode(mode));
                    dispatch(actions.copyLessonToTeamsSuccess());
                    dispatch(actions.closeModal());
                },
            })
        );
    } as unknown as Action);

    yield put(lessonActions.lessonUpdated());
}

export function* copyLessonAsRNOSaga(action: ReturnType<typeof actions.copyLessonAsRNO>) {
    const { sourceId, newName } = action.payload;
    const teamId: number = yield getTeamIdFromPath();

    // TODO: зачем перезапрашивать me до запроса?
    // Просто принудительное обновление me?
    const me: Me = yield call(api.getMe);
    yield put(setMe(me));

    const { viewed_hints: viewedHints } = me;
    const copiedAsRNOCount = viewedHints.wizard.variables[COPIED_AS_RNO_COUNT];
    const newCopiedAsRNOCount = (copiedAsRNOCount || 0) + 1;

    try {
        yield copyLessonToTeam({
            sourceId,
            newName,
            teamId,
            creationType: CREATION_TYPE.COPY_AS_RNO,
            replaceProblems: true,
            metrikaParams: {
                clesson_id: sourceId,
            },
        });
    } catch (error) {
        console.error(error);
        yield put(actions.copyLessonToTeamsError());
        yield put(showNetworkError(error));
    }

    yield put(setWizardVariables({ [COPIED_AS_RNO_COUNT]: newCopiedAsRNOCount }));
}

export function* copyLessonSaga(action: ReturnType<typeof actions.copyLessonToTeamsRequest>) {
    const { teamIds, intl, history } = action.payload;

    try {
        const currentTeamId = getTeamIdFromPath();
        const sourceCourseId: number | null = yield select(getCurrentYearCourseId);
        const sourceId: number | null = yield select(selectors.getLessonSourceId);
        const newName: string | null = yield select(selectors.getLessonCopyName);
        const activeSubscriptionSlug: string | null = yield select(lessonSelectors.getLessonActiveSubscriptionSlug, {
            courseId: sourceCourseId,
        });

        if (!sourceId || !newName) {
            return;
        }

        const metrikaParams = {
            clessonId: sourceId,
            subscription_slug: activeSubscriptionSlug,
        };

        if (teamIds.length === 1 && teamIds[0] === currentTeamId) {
            yield copyLessonToTeam({
                sourceId,
                newName,
                teamId: teamIds[0],
                metrikaParams,
            });
        } else {
            ym('reachGoal', 'teacher_lesson_copy_otherclasses', metrikaParams);

            const { set }: { set: CopyLessonResponse } = yield call(api.postCopyCLesson, sourceId, newName, teamIds);

            yield put(actions.copyLessonToTeamsSuccess());
            // если модалка занятия была открыта(копировали из нее), то закрываем
            yield put(lessonViewActions.toggleModal(false));
            yield put(actions.closeModal());
            yield put(lessonActions.clearEditor());

            yield put(
                actions.showLessonCopiedToClassesToast({
                    cLessonIds: set.map((item) => item.clesson_id),
                    teamIds,
                    intl,
                    history,
                }) as unknown as Action
            );

            for (const item of set) {
                yield put(coursesActions.clearCourseLessons({ courseId: item.course_id }));
                yield put(coursesActions.clearCourseLessonCount({ courseId: item.course_id }));
            }
        }
    } catch (error) {
        console.error(error);
        yield put(actions.copyLessonToTeamsError());
        yield put(showNetworkError(error));
    }
}
